import 'swiper/css';
import 'swiper/css/pagination';

import { useId, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import Icon, { IconName } from '@/components/Icon/Icon';
import { SelectedImage } from '@/components/ProductPageBase/SelectedImage/SelectedImage';
import useViewPortSize from '@/hooks/useViewportSize';

import useStyles from './Carousel.styles';

export interface CarouselProps {
  images: string[];
  selectedImageOverride?: string | undefined;
}

export const Carousel: React.FC<CarouselProps> = ({
  images,
  selectedImageOverride,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [shouldHideNavigation, setShouldHideNavigation] =
    useState<boolean>(false);
  const { classes, cx } = useStyles();
  const swiperRef = useRef<SwiperClass>(null);
  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');
  const paginationId = useId().replaceAll(':', '');

  const { isExtraSmall } = useViewPortSize();

  return (
    <div className={classes.container}>
      {!isExtraSmall && (
        <SelectedImage
          image={selectedImageOverride ?? images[selectedImageIndex]}
          zoomImageOnHover={true}
        />
      )}

      {images.length > 1 && (
        <>
          <div className={classes.carousel}>
            <div
              id={prevElId}
              className={cx(
                classes.navigationButton,
                shouldHideNavigation && classes.hidden
              )}
            >
              <Icon name={IconName.ARROW_LEFT} width={24} />
            </div>
            <div className={classes.swiperContainer}>
              <Swiper
                navigation={{
                  prevEl: `#${prevElId}`,
                  nextEl: `#${nextElId}`,
                  disabledClass: classes.navigationButtonDisabled,
                }}
                modules={[Navigation, Pagination]}
                slidesPerView={'auto'}
                onSwiper={(swiper: SwiperClass) => {
                  swiperRef.current = swiper;

                  if (swiper.isBeginning && swiper.isEnd) {
                    setShouldHideNavigation(true);
                  }
                }}
                pagination={{
                  el: `#${paginationId}`,
                  clickable: true,
                  bulletActiveClass: 'bullet-active',
                  bulletClass: 'bullet',
                  horizontalClass: classes.horizontal,
                  renderBullet: (index: number, className: string) => {
                    return `<svg class="${className}" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="4" cy="4" r="4" fill="currentColor"/>
                </svg>`;
                  },
                }}
              >
                {images.map((image, index) => (
                  <SwiperSlide className={classes.swiperSlide} key={index}>
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <img src={image} className={classes.carouselImage} />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <div
              id={nextElId}
              className={cx(
                classes.navigationButton,
                shouldHideNavigation && classes.hidden
              )}
            >
              <Icon name={IconName.ARROW_RIGHT} width={24} />
            </div>
          </div>
          <div className={classes.paginationContainer}>
            <div id={paginationId} className={classes.pagination}></div>
          </div>
        </>
      )}
    </div>
  );
};
